// src/pages/NivelTest.js
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { useFuncion } from '../context/FuncionContext';
import { useAuth } from '../context/AuthContext';
import { Clock, ChevronLeft, ChevronRight, Send, CheckCircle2 } from 'lucide-react';
import PreguntaExamTestRenderer from './tareas/renderers/PreguntaExamTestRenderer';
import useExamKiosk from "../hooks/useExamKiosk";
import useNivelTestProctor from "../proctoring/nivelTest/useNivelTestProctor";
import NivelTestProctorPanel from "../proctoring/nivelTest/NivelTestProctorPanel";
import ConfirmActionModal from "../components/assessment/ConfirmActionModal";
import AssessmentResultCard from "../components/assessment/AssessmentResultCard";
import cacheManager from "../utils/cacheManager";
import { invalidateByPrefix } from "../services/cacheService";

// ↓↓↓ pon esto debajo de los imports
const ensureJSON = (v, def = {}) => {
  if (v == null) return def;
  if (typeof v === 'string') {
    try { return JSON.parse(v); } catch { return def; }
  }
  return (typeof v === 'object') ? v : def;
};

// Adapta diferentes shapes a lo que espera NivelTest
const pickUid = (raw = {}) =>
  raw.uid ??
  raw.question_uid ??
  raw.qid ??
  raw.pregunta_uid ??
  raw.preguntaId ??
  raw.pregunta_id ??
  raw.id;

const pickCourseId = (m = {}) =>
  m?.cursoId ??
  m?.courseId ??
  m?.idCurso ??
  m?.curso_id ??
  m?.curso?.id ??
  m?.course?.id ??
  null;

const CAMERA_HEALTH_LIMITS = {
  sampleMs: 500,
  width: 192,
  height: 144,
  darkLumaMax: 30,
  stableLumaMin: 35,
  stableLumaMax: 240,
  overBrightLumaMin: 245,
  overBrightRatioMax: 0.35,
  contrastMin: 12,
  centerContrastMin: 8,
  centerLumaMin: 30,
  motionDiffMin: 0.65,
  faceDetectEveryMs: 800,
  faceMinAreaPct: 0.02,
  faceMaxAreaPct: 0.75,
  faceCenterTolerance: 0.65,
  faceMinScore: 0.30,
  allowQualityFallbackAsFace: true,
  videoReadyTimeoutMs: 5000,
  verifyDurationMs: 7200,
  verifyMinFrames: 8,
  verifyMinMotionHits: 2,
  verifyMinFaceHits: 1,
  verifyMinFaceChecks: 2,
  darkGraceMs: 2200,
  noMotionGraceMs: 10000,
};

const getCameraValidationLabel = (health = {}) => {
  if (health?.validating) return "Validando cámara...";
  if (health?.validated) return "Cámara validada";
  if (health?.videoReady && health?.ok) return "Lista para validar";
  if (health?.videoReady) return "Ajusta tu cámara";
  return "Esperando cámara";
};

const getCameraValidationTone = (health = {}) => {
  if (health?.validating) return "checking";
  if (health?.validated) return "ok";
  if (health?.videoReady && health?.ok) return "checking";
  if (health?.videoReady === false || health?.ok === false) return "bad";
  return "";
};

const cameraCheckOk = (value) => value === true;
const cameraCheckBad = (value) => value === false;

const hasMeaningfulAnswer = (value) => {
  if (value == null) return false;
  if (Array.isArray(value)) return value.some((item) => hasMeaningfulAnswer(item));
  if (typeof value === "object") return Object.keys(value).length > 0;
  return String(value).trim() !== "";
};


const toStudentQ = (raw = {}) => {
  const uid = pickUid(raw);
  if (uid == null) {
    console.error("❌ Pregunta sin uid/id desde backend:", raw);
    return {
      id: "__MISSING_UID__",
      enunciado: raw.enunciado ?? raw.pregunta ?? raw.texto ?? '',
      tipo: (raw.tipo ?? raw.tipo_pregunta ?? raw.kind ?? '').toLowerCase(),
      opciones: ensureJSON(raw.opciones, ensureJSON(raw.options, ensureJSON(raw.config, {}))),
      puntos: Number(raw.puntos ?? 1),
    };
  }

  return {
    id: String(uid),
    enunciado: raw.enunciado ?? raw.pregunta ?? raw.texto ?? '',
    tipo: (raw.tipo ?? raw.tipo_pregunta ?? raw.kind ?? '').toLowerCase(),
    opciones: ensureJSON(raw.opciones, ensureJSON(raw.options, ensureJSON(raw.config, {}))),
    puntos: Number(raw.puntos ?? 1),
  };
};

// Intenta obtener la lista *ya resuelta* tal como hace Examen
const mergeResolvedQuestions = (data = {}) => {
  const candidates =
    [
      data.questionsResolved,
      data.resolvedQuestions,
      data.resueltas,
      data.resultado,
      data.questions,
      data.preguntasResueltas,
      data.resolved,
      data.preguntas
    ].find(Array.isArray) || [];
  return candidates.map(toStudentQ);
};

/**
 * Tipos soportados (enum):
 * 'seleccion_multiple' | 'emparejamiento' | 'ordenamiento'
 * 'verdadero_falso'   | 'respuesta_corta' | 'completar'
 */

const NivelTest = () => {
  const { codigoCurso, presentacionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setHeaderVisible, setFooterVisible } = useOutletContext() || {};
  const { user } = useAuth();

  const {
    getPresentationQuestions,
    finishPresentation,
    getStudentUTMCoins,
    savePresentationProgress,
    getLevelTestStatus,
    getStudentStreak,
    initializeStudentData,
    uploadProctorSnapshot,
    resolveStudentCourseByCode,
  } = useFuncion();

  const [loading, setLoading] = useState(true);
  const [courseId, setCourseId] = useState(null);
  const [meta, setMeta] = useState(null);
  const [requiresCamera, setRequiresCamera] = useState(true);
  const [preguntas, setPreguntas] = useState([]);
  const [index, setIndex] = useState(0);
  const finishingRef = useRef(false);
  const lastSavedRef = useRef('');

  const [coinInfo, setCoinInfo] = useState({ ready: false, eligible: false, premio: 0 });
  const [resumeContext, setResumeContext] = useState({
    isResume: false,
    answeredCount: 0,
    total: 0,
    currentIndex: 0,
  });

  const [respuestas, setRespuestas] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [finishConfirmOpen, setFinishConfirmOpen] = useState(false);

  const [preflightOpen, setPreflightOpen] = useState(
    !!location.state?.openPreflight
  );
  const [preflightChecked, setPreflightChecked] = useState(false);
  const [secureModeReady, setSecureModeReady] = useState(false);
  const [preflightStarting, setPreflightStarting] = useState(false);
  const [restoringSecureMode, setRestoringSecureMode] = useState(false);

  const [switchingCamera, setSwitchingCamera] = useState(false);
  const initialShotScheduledRef = useRef(false);

  const KIOSK_MAX_TOTAL = 6;

  const kioskStorageKey = useMemo(
    () => `nivel_test:kiosk:${presentacionId}`,
    [presentacionId]
  );

  const enableKiosk = !loading && !resultado && !!preguntas?.length;

  const kiosk = useExamKiosk({
    maxViolations: KIOSK_MAX_TOTAL,
    storageKey: kioskStorageKey,
    repeatStrikeMs: 4500,
    onViolation: () => { },
    onTooManyViolations: async () => { },
    onAttentionRequired: () => { },
    countPageHide: true,
    preventDefaultOnHotkeys: true,
  });

  const enterFullscreenSafe = useCallback(async () => {
    try {
      const el = document.documentElement;

      if (el.requestFullscreen) {
        await el.requestFullscreen();
        return true;
      }
      if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
        return true;
      }
      if (el.msRequestFullscreen) {
        el.msRequestFullscreen();
        return true;
      }
    } catch { }
    return false;
  }, []);

  const exitFullscreenSafe = useCallback(async () => {
    try {
      const d = document;

      if (d.fullscreenElement && d.exitFullscreen) {
        await d.exitFullscreen();
        return;
      }
      if (d.webkitFullscreenElement && d.webkitExitFullscreen) {
        d.webkitExitFullscreen();
        return;
      }
      if (d.msFullscreenElement && d.msExitFullscreen) {
        d.msExitFullscreen();
        return;
      }
    } catch { }
  }, []);

  // ===================== PROCTORING NIVEL TEST (2 FOTOS) =====================
  const REQUIRE_PROCTOR = !!requiresCamera;
  const FIRST_SNAPSHOT_DELAY_MS = 6000;

  const {
    proctor,
    deviceOpts: proctorDevices,
    videoRef: proctorVideoRef,
    start: startProctor,
    stop: stopProctor,
    retry: retryProctor,
    setSelectedCam,
    captureBlob,
    refreshDeviceList: refreshProctorDevices,
    cameraHealth,
    verifyCameraBeforeExam,
  } = useNivelTestProctor({ enabled: REQUIRE_PROCTOR });

  const validateCameraFromModal = async () => {
    if (!verifyCameraBeforeExam) return;
    try {
      setPreflightChecked(false);
      const ok = await retryProctor(selectedCamRef.current || "");
      if (!ok) return;
      const validated = await verifyCameraBeforeExam({ durationMs: 7200 });
      if (validated) {
        markCurrentCameraAsValidated();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const [selectedCameraId, setSelectedCameraId] = useState("");
  const cameraValidationSessionKey = useMemo(() => {
    const uid = Number(user?.id || 0) || "anon";
    return `nivel_test:cam-valid:${presentacionId}:${uid}`;
  }, [presentacionId, user?.id]);

  const selectedCamRef = useRef("");

  const readValidatedCamera = useCallback(() => {
    try {
      const raw = JSON.parse(sessionStorage.getItem(cameraValidationSessionKey) || "null");
      if (!raw?.at) return null;
      if (Date.now() - Number(raw.at) > 30 * 60 * 1000) {
        sessionStorage.removeItem(cameraValidationSessionKey);
        return null;
      }
      return raw;
    } catch {
      return null;
    }
  }, [cameraValidationSessionKey]);

  const clearValidatedCamera = useCallback(() => {
    try {
      sessionStorage.removeItem(cameraValidationSessionKey);
    } catch {}
  }, [cameraValidationSessionKey]);

  const markCurrentCameraAsValidated = useCallback(() => {
    const camId =
      selectedCamRef.current ||
      selectedCameraId ||
      proctorDevices?.camId ||
      "";

    try {
      sessionStorage.setItem(
        cameraValidationSessionKey,
        JSON.stringify({
          camId: camId || null,
          at: Date.now(),
        })
      );
    } catch {}
  }, [cameraValidationSessionKey, proctorDevices?.camId, selectedCameraId]);

  const currentCameraAlreadyValidated = useCallback(() => {
    const cached = readValidatedCamera();
    if (!cached?.at) return false;

    const currentCamId =
      selectedCamRef.current ||
      selectedCameraId ||
      proctorDevices?.camId ||
      "";

    if (!cached.camId || !currentCamId) return true;
    return String(cached.camId) === String(currentCamId);
  }, [proctorDevices?.camId, readValidatedCamera, selectedCameraId]);

  const handleCameraChange = useCallback(async (camId) => {
    const nextId = camId || "";
    if (switchingCamera) return;

    setSwitchingCamera(true);
    try {
      setPreflightChecked(false);
      clearValidatedCamera();
      setSelectedCameraId(nextId);
      setSelectedCam(nextId);
      await retryProctor(nextId);
    } catch (e) {
      console.error("No se pudo cambiar de cámara:", e);
      alert("No se pudo cambiar a la cámara seleccionada.");
    } finally {
      setSwitchingCamera(false);
    }
  }, [clearValidatedCamera, retryProctor, setSelectedCam, switchingCamera]);

  useEffect(() => {
    selectedCamRef.current = selectedCameraId || "";
  }, [selectedCameraId]);

  useEffect(() => {
    if (!selectedCameraId && proctorDevices?.camId) {
      setSelectedCameraId(proctorDevices.camId);
    }
  }, [selectedCameraId, proctorDevices?.camId]);

  useEffect(() => {
    if (!REQUIRE_PROCTOR || !preflightOpen) return;

    let cancelled = false;

    const bootCamera = async () => {
      try {
        await refreshProctorDevicesRef.current?.();
        if (cancelled) return;

        if (!proctor?.cam) {
          await startProctorRef.current?.(selectedCamRef.current || "");
        }
      } catch (e) {
        if (!cancelled) {
          console.error("No se pudo preparar la cámara del preflight:", e);
        }
      }
    };

    bootCamera();

    return () => {
      cancelled = true;
    };
  }, [REQUIRE_PROCTOR, preflightOpen, proctor?.cam]);

  const activateSecureMode = useCallback(async ({ fromRestore = false } = {}) => {
    const setBusy = fromRestore ? setRestoringSecureMode : setPreflightStarting;
    setBusy(true);

    try {
      if (!fromRestore) {
        setSecureModeReady(false);
      }

      if (REQUIRE_PROCTOR) {
        await refreshProctorDevicesRef.current?.();

        const desiredCamId = selectedCamRef.current || "";
        const ok = await startProctorRef.current?.(desiredCamId);

        if (!ok) {
          alert("No se pudo activar la cámara. Debes permitirla para continuar.");
          return false;
        }

        setSelectedCameraId(desiredCamId);
      }
      await enterFullscreenSafe();
      await new Promise((r) => setTimeout(r, 120));

      if (typeof kioskRef.current?.requestAttentionRestore === "function") {
        await kioskRef.current.requestAttentionRestore();
      }

      if (typeof kioskRef.current?.start === "function") {
        await kioskRef.current.start();
      }

      setSecureModeReady(true);

      if (!fromRestore) {
        setPreflightOpen(false);
      }

      return true;
    } catch (e) {
      console.error(e);
      alert("No se pudo activar el modo seguro del test.");
      return false;
    } finally {
      setBusy(false);
    }
  }, [REQUIRE_PROCTOR, enterFullscreenSafe]);

  const handleStartSecureMode = useCallback(async () => {
    const ok = await activateSecureMode({ fromRestore: false });
    if (!ok) return;
  }, [activateSecureMode]);

  // Timer
  const [secondsLeft, setSecondsLeft] = useState(null);
  const timerRef = useRef(null);

  const respuestasRef = useRef(respuestas);
  const preguntasRef = useRef(preguntas);
  const indexRef = useRef(index);

  const kioskRef = useRef(kiosk);
  const stopProctorRef = useRef(stopProctor);

  useEffect(() => {
    kioskRef.current = kiosk;
  }, [kiosk]);

  useEffect(() => {
    stopProctorRef.current = stopProctor;
  }, [stopProctor]);

  useEffect(() => {
    return () => {
      try {
        if (startShotTimerRef.current) {
          clearTimeout(startShotTimerRef.current);
          startShotTimerRef.current = null;
        }
      } catch { }
      try { kioskRef.current?.stop?.(true); } catch { }
      try { exitFullscreenSafe(); } catch { }
      try { stopProctorRef.current?.(); } catch { }
    };
  }, [exitFullscreenSafe]);

  useEffect(() => { respuestasRef.current = respuestas; }, [respuestas]);
  useEffect(() => { preguntasRef.current = preguntas; }, [preguntas]);
  useEffect(() => { indexRef.current = index; }, [index]);

  const blockedByProctor =
    REQUIRE_PROCTOR && secureModeReady && !preflightOpen && !!proctor?.blocked;

  const blockedByKiosk =
    enableKiosk && secureModeReady && !preflightOpen && !!kiosk?.blocked;

  const interactionBlocked =
    preflightOpen ||
    !secureModeReady ||
    blockedByProctor ||
    blockedByKiosk;
  const effectiveCameraValidated =
    !REQUIRE_PROCTOR ||
    cameraHealth?.validated === true ||
    currentCameraAlreadyValidated();

  const shouldLockTestChrome = !resultado;
  const shouldProtectExit = secureModeReady && !preflightOpen && !resultado && !!preguntas?.length;

  useEffect(() => {
    if (shouldLockTestChrome) {
      setHeaderVisible?.(false);
      setFooterVisible?.(false);
    } else {
      setHeaderVisible?.(true);
      setFooterVisible?.(true);
    }

    return () => {
      setHeaderVisible?.(true);
      setFooterVisible?.(true);
    };
  }, [setFooterVisible, setHeaderVisible, shouldLockTestChrome]);

  useEffect(() => {
    document.body.classList.toggle("exam-focus", !!shouldLockTestChrome);
    return () => {
      document.body.classList.remove("exam-focus");
    };
  }, [shouldLockTestChrome]);

  useEffect(() => {
    if (!shouldProtectExit) return undefined;

    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    try {
      window.history.pushState({ nivelTestGuard: true }, "", currentUrl);
    } catch {}

    const onPopState = () => {
      try {
        window.history.pushState({ nivelTestGuard: true }, "", currentUrl);
      } catch {}
      window.alert("Debes finalizar o enviar el test de nivel antes de salir.");
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [shouldProtectExit]);

  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();

    (async () => {
      try {
        if (!codigoCurso) return;

        const c = await resolveStudentCourseByCode?.(codigoCurso, { signal: ctrl.signal });
        if (!alive) return;
        setCourseId(c?.id ? Number(c.id) : null);
      } catch (e) {
        if (!alive) return;
        console.warn("No se pudo resolver courseId:", e?.message || e);
        setCourseId(null);
      }
    })();

    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [codigoCurso, resolveStudentCourseByCode]);

  const firstShotRef = useRef(false);
  const secondShotRef = useRef(false);
  const startShotTimerRef = useRef(null);

  const sendSnapshot = useCallback(
    async (tag = "snapshot") => {
      try {
        if (!REQUIRE_PROCTOR) return false;
        if (proctor?.blocked) return false;

        // ✅ Para nivel_test, necesitamos sí o sí estos IDs (o al menos testPresentacionId)
        const testPresentacionIdNum = Number(presentacionId);
        if (!Number.isFinite(testPresentacionIdNum) || testPresentacionIdNum <= 0) {
          console.warn("[NivelTest] snapshot omitido: testPresentacionId inválido", presentacionId);
          return false;
        }

        // Si tu BD requiere course_id en nivel_test, esto evita enviar null
        if (!courseId) {
          console.warn("[NivelTest] snapshot omitido: courseId aún no está listo");
          return false;
        }

        const blob = await captureBlob();
        if (!blob) {
          console.warn("[NivelTest] snapshot omitido: captureBlob() devolvió null");
          return false;
        }

        const payload = {
          blob,
          presentacionId: null,
          examId: null,
          courseId: Number(courseId),

          context: "test_nivel",
          testPresentacionId: testPresentacionIdNum,
          testId: meta?.testId ?? meta?.test_id ?? null,

          tag,
          codigoCurso,
          meta: { nivelId: meta?.nivelId ?? meta?.nivel_id ?? null },

          mic: null,   // ✅ no enviar mic
          final: 0,
        };

        const resp = await uploadProctorSnapshot?.(payload);

        // ✅ Si tu backend devuelve ok_db, úsalo para enterarte
        const okDb = !!(resp?.ok_db ?? resp?.okDb);
        if (!okDb) {
          console.warn("[NivelTest] Snapshot subió archivo pero NO insertó BD:", resp);
          return false;
        }

        return true;
      } catch (e) {
        console.error("[NivelTest] sendSnapshot error:", e?.message || e);
        return false;
      }
    },
    [
      REQUIRE_PROCTOR,
      proctor?.blocked,
      captureBlob,
      uploadProctorSnapshot,

      // ✅ IMPORTANTÍSIMO: antes no estaba
      courseId,

      // ✅ también inclúyelos para no “capturar” valores viejos
      presentacionId,
      codigoCurso,
      meta,
    ]
  );


  const total = preguntas.length;
  const current = preguntas[index];

  const answeredCount = useMemo(() => {
    return preguntas.reduce((acc, q) => {
      const v = respuestas[q.id];
      if (v == null) return acc;
      if (Array.isArray(v)) return acc + (v.length > 0 ? 1 : 0);
      return acc + (String(v).trim() ? 1 : 0);
    }, 0);
  }, [respuestas, preguntas]);

  const isFirst = index === 0;
  const isLast = index === total - 1;

  const progressPct = total ? Math.round((answeredCount / total) * 100) : 0;

  const toNum2 = (v, fallback = 0) => {
    if (v == null || v === "") return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const normalizeResult = (res = {}) => {
    const puntuacion_obtenida = toNum2(
      res.puntuacion_obtenida ?? res.puntuacionObtenida ?? res.score_obtained ?? res.scoreObtained,
      0
    );

    const puntuacion_maxima = toNum2(
      res.puntuacion_maxima ?? res.puntuacionMaxima ?? res.score_max ?? res.scoreMax,
      0
    );

    const notaMax = toNum2(
      res.nota_maxima ?? res.notaMaxima ?? meta?.notaMaxima ?? meta?.nota_maxima,
      NaN
    );
    const puntuacion_maxima_final =
      (puntuacion_maxima > 0) ? puntuacion_maxima
        : (Number.isFinite(notaMax) && notaMax > 0 ? notaMax : puntuacion_maxima);

    // si backend manda porcentaje, úsalo; si no, calcúlalo defensivo
    const porcentaje = (() => {
      const p = toNum2(res.porcentaje_obtenido ?? res.porcentajeObtenido ?? res.puntaje ?? res.porcentaje, NaN);
      if (Number.isFinite(p)) return p;
      if (puntuacion_maxima > 0) return Math.round((puntuacion_obtenida / puntuacion_maxima) * 10000) / 100;
      return 0;
    })();

    return {
      porcentaje,
      aprobado: !!(res.aprobado ?? res.isPassed ?? res.passed),
      puntuacion_obtenida,
      puntuacion_maxima: puntuacion_maxima_final,
      // opcional (si backend manda)
      respuestas_correctas: toNum2(res.respuestas_correctas ?? res.respuestasCorrectas, NaN),
      respuestas_incorrectas: toNum2(res.respuestas_incorrectas ?? res.respuestasIncorrectas, NaN),
      preguntas_totales: toNum2(
        res.preguntas_totales ??
        res.preguntasTotales ??
        meta?.numeroPreguntas ??
        meta?.numero_preguntas,
        NaN
      ),
      tiempo_empleado_segundos: toNum2(res.tiempo_empleado_segundos ?? res.tiempoEmpleadoSegundos ?? res.tiempoEmpleadoSeg, 0),
      intento_ordinal: toNum2(res.intento_ordinal ?? res.intentoOrdinal, 1),
      eligible_coins: !!(res.eligible_coins ?? res.eligibleCoins ?? false),
      utmcoins_awarded: toNum2(res.utmcoins_awarded ?? res.utmcoinsAwarded ?? 0, 0),
      premio_utm: toNum2(res.premio_utm ?? res.premioUTM ?? res.utmcoinsPremio ?? 0, 0),
      penalty_applied: res.penalty_applied ?? res.penaltyApplied ?? null,
    };
  };

  // ====== Sanitizador de payload ======
  const isIntString = (s) => /^-?\d+$/.test(String(s).trim());
  const castArrayMaybeNumber = (arr) => {
    const a = arr.map(v => String(v).trim()).filter(Boolean);
    return a.length && a.every(isIntString) ? a.map(Number) : a;
  };

  const sanitizeForSchema = (q, raw) => {
    const t = (q?.tipo || '').toLowerCase();
    if (raw == null) return null;

    if (t === 'seleccion_multiple') {
      const arr = Array.isArray(raw) ? raw : [raw];
      const casted = castArrayMaybeNumber(arr);
      return casted.length ? casted : null;
    }

    if (t === 'verdadero_falso') {
      const s = String(raw).trim().toLowerCase();
      if (s === 'verdadero' || s === 'falso') return s;

      if (s === '1' || s === '2') {
        const ops = ensureJSON(q?.opciones, {});
        const list = Array.isArray(ops?.opciones) ? ops.opciones : (Array.isArray(ops) ? ops : []);
        const idx = Number(s) - 1;
        const mapped = list[idx]?.valor ?? list[idx]?.value ?? null;
        if (mapped) return String(mapped).trim().toLowerCase();
      }

      return s || null;
    }

    if (t === 'ordenamiento') {
      const arr = Array.isArray(raw) ? raw : [raw];
      const casted = castArrayMaybeNumber(arr);
      return casted.length ? casted : null;
    }

    if (t === 'completar') {
      const arr = Array.isArray(raw) ? raw : [raw];
      const clean = arr.map(v => String(v).trim()).filter(Boolean);
      return clean.length ? clean : null;
    }

    if (t === 'emparejamiento') {
      const arr = Array.isArray(raw) ? raw : [raw];
      const clean = arr.map(v => String(v).trim()).filter(s => !!s && s.includes('|'));
      return clean.length ? clean : null;
    }

    if (t === 'respuesta_corta') {
      const s = String(raw).trim();
      return s || null;
    }

    if (t === 'seleccion_simple' || t === 'single_choice' || t === 'multiple_choice') {
      const v = Array.isArray(raw) ? raw[0] : raw;
      const s = String(v ?? '').trim();
      return isIntString(s) ? Number(s) : (s || null);
    }

    const s = String(raw).trim();
    return s || null;
  };

  // ====== Carga de preguntas ======
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPresentationQuestions(presentacionId);
      setMeta(data.presentacion);
      const pres = data?.presentacion || null;
      setRequiresCamera(
        pres?.requiereCamara ??
        pres?.requiere_camara ??
        true
      );

      // Normaliza notaMaxima (viene del backend en tests/presentations/:id/questions)
      const notaMaxima = (() => {
        const v = pres?.notaMaxima ?? pres?.nota_maxima ?? null;
        if (v == null || v === '') return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      })();

      setMeta(pres ? { ...pres, notaMaxima } : pres);

      // ▶️ Si el intento ya terminó, mostrar resultado y salir
      if (data.presentacion?.estado && data.presentacion.estado !== 'en_progreso') {
        const resultFromMeta = normalizeResult({
          porcentaje_obtenido: data.presentacion.porcentaje_obtenido ?? data.presentacion.porcentajeObtenido,
          puntuacion_obtenida: data.presentacion.puntuacion_obtenida ?? data.presentacion.puntuacionObtenida,
          puntuacion_maxima: data.presentacion.puntuacion_maxima ?? data.presentacion.puntuacionMaxima,
          aprobado: data.presentacion.aprobado,
          respuestas_correctas: data.presentacion.respuestas_correctas ?? data.presentacion.respuestasCorrectas,
          respuestas_incorrectas: data.presentacion.respuestas_incorrectas ?? data.presentacion.respuestasIncorrectas,
          preguntas_totales: data.presentacion.preguntas_totales ?? data.presentacion.preguntasTotales,
          tiempo_empleado_segundos: data.presentacion.tiempo_empleado_segundos ?? data.presentacion.tiempoEmpleadoSeg,
          intento_ordinal: data.presentacion.intento_ordinal ?? data.presentacion.intentoOrdinal,
          eligible_coins: data.presentacion.eligible_coins ?? data.presentacion.eligibleCoins ?? false,
          utmcoins_awarded: data.presentacion.utmcoins_awarded ?? data.presentacion.utmcoinsAwarded ?? 0,
          premio_utm: data.presentacion.premio_utm ?? data.presentacion.premioUTM ?? data.presentacion.utmcoinsPremio ?? 0,
        });

        setResultado(resultFromMeta);
        setPreguntas([]);
        setRespuestas({});
        setSecondsLeft(null);
        return;
      }
      // --- Tiempo desde servidor (NO reiniciar)
      const dl = data.presentacion?.deadlineAt || data.presentacion?.fecha_limite;
      const nowSrv = data.presentacion?.serverNow;

      if (dl) {
        const serverNow = nowSrv ? new Date(nowSrv) : new Date();
        const rem = Math.max(0, Math.floor((new Date(dl) - serverNow) / 1000));
        setSecondsLeft(rem);
      } else if (Number.isFinite(data.presentacion?.secondsLeft)) {
        setSecondsLeft(Number(data.presentacion.secondsLeft));
      } else if (Number.isFinite(data.presentacion?.tiempoRestanteSeg)) {
        setSecondsLeft(Number(data.presentacion.tiempoRestanteSeg));
      } else if (data.presentacion?.tiempoLimiteMin) {
        setSecondsLeft(Number(data.presentacion.tiempoLimiteMin) * 60);
      }

      // --- UTMCoins
      const prem = Number(
        data.presentacion?.premioUTM ??
        data.presentacion?.utmcoinsPremio ?? 0
      );

      if (typeof data.presentacion?.eligibleCoins === 'boolean') {
        setCoinInfo({ ready: true, eligible: !!data.presentacion.eligibleCoins, premio: prem });
      } else if (data.presentacion?.nivelId) {
        try {
          const st = await getLevelTestStatus(data.presentacion.nivelId);
          setCoinInfo({
            ready: true,
            eligible: !!st?.eligibleCoins,
            premio: Number(st?.premioUTM ?? prem)
          });
        } catch {
          setCoinInfo({ ready: false, eligible: false, premio: prem || 0 });
        }
      } else {
        setCoinInfo({ ready: false, eligible: false, premio: prem || 0 });
      }

      // --- Lista final de preguntas
      const resolvedList = mergeResolvedQuestions(data);
      const finalQs = resolvedList.length
        ? resolvedList
        : (Array.isArray(data.preguntas) ? data.preguntas.map(toStudentQ) : []);
      const configuredCount = Number(
        data.presentacion?.numeroPreguntas ??
        data.presentacion?.numero_preguntas ??
        0
      );
      const normalizedQuestions =
        Number.isFinite(configuredCount) && configuredCount > 0
          ? finalQs.slice(0, configuredCount)
          : finalQs;
      setPreguntas(normalizedQuestions);

      // --- Restaurar respuestas/índice
      const rp = data.presentacion?.respuestasParciales || {};
      const restored = (rp && typeof rp === 'object' && 'draft' in rp)
        ? (rp.draft || {})
        : rp || {};

      const normalized = Object.fromEntries(
        Object.entries(restored).map(([k, v]) => [String(k), v])
      );
      setRespuestas(normalized);

      const idxFromDraft = Number(rp?.index);
      let restoredIndex = 0;
      if (Number.isFinite(idxFromDraft)) {
        restoredIndex = Math.min(Math.max(0, idxFromDraft), Math.max(0, normalizedQuestions.length - 1));
        setIndex(restoredIndex);
      } else if (Number.isInteger(data.presentacion?.posicionActual)) {
        restoredIndex = Math.min(
          Math.max(0, data.presentacion.posicionActual),
          Math.max(0, normalizedQuestions.length - 1)
        );
        setIndex(restoredIndex);
      }

      const answeredCount = Object.values(normalized).filter((value) => hasMeaningfulAnswer(value)).length;
      const isResume = answeredCount > 0 || restoredIndex > 0 || !!location.state?.resumeAttempt;
      setResumeContext({
        isResume,
        answeredCount,
        total: normalizedQuestions.length,
        currentIndex: restoredIndex,
      });

      if (!secureModeReady) {
        setPreflightOpen(true);
      }
    } catch (e) {
      console.error(e);
      alert(e.message || 'No se pudieron cargar las preguntas');
      navigate(`/curso/${codigoCurso}`);
    } finally {
      setLoading(false);
    }
  }, [presentacionId, getPresentationQuestions, getLevelTestStatus, navigate, codigoCurso, location.state?.resumeAttempt, secureModeReady]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startProctorRef = useRef(startProctor);
  const refreshProctorDevicesRef = useRef(refreshProctorDevices);
  const sendSnapshotRef = useRef(sendSnapshot);

  useEffect(() => {
    startProctorRef.current = startProctor;
  }, [startProctor]);

  useEffect(() => {
    refreshProctorDevicesRef.current = refreshProctorDevices;
  }, [refreshProctorDevices]);

  useEffect(() => {
    sendSnapshotRef.current = sendSnapshot;
  }, [sendSnapshot]);

  // ====== Start proctoring when test is active + schedule photo #1 ======
  useEffect(() => {
    if (!REQUIRE_PROCTOR) return;
    if (resultado) return;
    if (loading) return;
    if (!preguntas?.length) return;
    if (preflightOpen || !secureModeReady) return;
    if (!courseId) return;
    if (initialShotScheduledRef.current) return;

    initialShotScheduledRef.current = true;
    let cancelled = false;

    if (!firstShotRef.current) {
      firstShotRef.current = true;

      if (startShotTimerRef.current) {
        clearTimeout(startShotTimerRef.current);
      }

      startShotTimerRef.current = setTimeout(async () => {
        if (cancelled) return;
        await sendSnapshotRef.current?.("start");
      }, FIRST_SNAPSHOT_DELAY_MS);
    }

    return () => {
      cancelled = true;
      if (startShotTimerRef.current) clearTimeout(startShotTimerRef.current);
      startShotTimerRef.current = null;
      initialShotScheduledRef.current = false;
    };
  }, [
    REQUIRE_PROCTOR,
    loading,
    preguntas?.length,
    resultado,
    preflightOpen,
    secureModeReady,
    courseId,
    FIRST_SNAPSHOT_DELAY_MS,
  ]);
  // ====== Finish ======
  const handleFinish = useCallback(async (auto = false, skipConfirm = false) => {
    if (finishingRef.current) return;
    if (!preguntasRef.current.length) return;

    if (!auto && !skipConfirm) {
      setFinishConfirmOpen(true);
      return;
    }

    finishingRef.current = true;
    setSubmitting(true);

    try {
      const preguntasNow = preguntasRef.current;
      const respuestasNow = respuestasRef.current;

      const payload = preguntasNow.map(q => {
        const v = sanitizeForSchema(q, respuestasNow[q.id]);
        return v === null
          ? { preguntaId: String(q.id) }
          : { preguntaId: String(q.id), respuesta: v };
      });

      try {
        if (REQUIRE_PROCTOR && !secondShotRef.current) {
          secondShotRef.current = true;
          const ok = await sendSnapshot(auto ? "auto_finish" : "finish");
          if (!ok) console.warn("[NivelTest] ❌ No se pudo guardar snapshot final en BD");
        }
      } catch { }

      const res = await finishPresentation(presentacionId, payload);
      const norm = normalizeResult(res);

      try { await getStudentStreak?.(); } catch { }
      try { await initializeStudentData?.(); } catch { }

      let merged = { ...norm };
      if (
        norm.aprobado &&
        Number(norm.utmcoins_awarded) === 0 &&
        coinInfo.ready && coinInfo.eligible && Number(coinInfo.premio) > 0
      ) {
        merged = {
          ...merged,
          eligible_coins: true,
          utmcoins_awarded: Number(coinInfo.premio),
          premio_utm: Number(coinInfo.premio)
        };
      }

      setResultado(merged);
      try {
        const rawUserScope = String(
          user?.local?.id ||
          user?.estudiante_id ||
          user?.student_id ||
          user?.id ||
          user?.email ||
          "anon"
        );
        const userScope = cacheManager.safeKey(rawUserScope);

        if (courseId) {
          cacheManager.invalidateCourse(courseId, userScope);
          invalidateByPrefix(`examBookings:${userScope}:/api/courses/${courseId}/structure`);
        }
        invalidateByPrefix(`examBookings:${userScope}:/api/courses/levels/`);
        invalidateByPrefix(`examBookings:${userScope}:/api/courses/student?`);
      } catch {}
      try {
        window.dispatchEvent(
          new CustomEvent("student-course-progress-updated", {
            detail: {
              codigoCurso,
              userScope: String(
                user?.local?.id ||
                user?.estudiante_id ||
                user?.student_id ||
                user?.id ||
                user?.email ||
                "anon"
              ).replace(/[^a-z0-9_-]/gi, "").slice(0, 48),
              presentacionId,
              nivelId: meta?.nivel_id ?? null,
              source: "nivel-test-finished",
            },
          })
        );
      } catch { }
      try { await getStudentUTMCoins(); } catch { }

      clearTimeout(timerRef.current);
      setSecondsLeft(0);
    } catch (e) {
      console.error(e);
      alert(e.message || "No se pudo finalizar el test");
    } finally {
      try {
        stopProctorRef.current?.();
        if (startShotTimerRef.current) {
          clearTimeout(startShotTimerRef.current);
          startShotTimerRef.current = null;
        }
      } catch { }

      try {
        await kiosk.stop?.(true);
      } catch { }

      try {
        await exitFullscreenSafe();
      } catch { }

      setSelectedCameraId("");
      setSubmitting(false);
      finishingRef.current = false;
      setFinishConfirmOpen(false);
    }
  }, [
    REQUIRE_PROCTOR,
    coinInfo,
    finishPresentation,
    getStudentStreak,
    getStudentUTMCoins,
    initializeStudentData,
    presentacionId,
    sendSnapshot,
    stopProctor,
    exitFullscreenSafe,
    kiosk
  ]);

  useEffect(() => {
    if (!enableKiosk || !kiosk?.tooManyViolations) return;
    if (!finishingRef.current && !submitting) {
      handleFinish(true);
    }
  }, [enableKiosk, kiosk?.tooManyViolations, submitting, handleFinish]);

  // ====== Countdown ======
  useEffect(() => {
    if (secondsLeft == null) return;

    if (secondsLeft <= 0) {
      if (!finishingRef.current && !submitting) handleFinish(true);
      return;
    }

    timerRef.current = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [
    secondsLeft,
    handleFinish,
    submitting,
  ]);

  const formatTime = (s) => {
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  };

  // ====== Autosave ======
  const doAutosave = useCallback(async (forcedIndex = null) => {
    const preguntasNow = preguntasRef.current;
    if (!preguntasNow.length) return;

    const idxToSave = Number.isInteger(forcedIndex) ? forcedIndex : indexRef.current;
    const respuestasNow = respuestasRef.current;

    const draft = preguntasNow.reduce((acc, q) => {
      const v = sanitizeForSchema(q, respuestasNow[q.id]);
      acc[String(q.id)] = (v === null ? null : v);
      return acc;
    }, {});

    const fingerprint = JSON.stringify({ draft, index: idxToSave });
    if (fingerprint === lastSavedRef.current) return;

    try {
      const payloadArray = Object.entries(draft).map(([preguntaId, v]) =>
        v == null
          ? { preguntaId: String(preguntaId) }
          : { preguntaId: String(preguntaId), respuesta: v }
      );

      await savePresentationProgress(presentacionId, payloadArray, idxToSave);
      lastSavedRef.current = fingerprint;
    } catch (e) {
      console.error('Autosave error:', e?.message || e);
    }
  }, [presentacionId, savePresentationProgress]);

  // ====== Navegación ======
  const handlePrev = async () => {
    if (interactionBlocked) return;
    const next = Math.max(index - 1, 0);
    await doAutosave(next);
    setIndex(next);
  };

  const handleNext = async () => {
    if (interactionBlocked) return;
    const next = Math.min(index + 1, total - 1);
    await doAutosave(next);
    setIndex(next);
  };

  const goTo = async (i) => {
    if (interactionBlocked) return;
    const next = Math.min(Math.max(0, i), Math.max(0, total - 1));
    await doAutosave(next);
    setIndex(next);
  };

  useEffect(() => {
    return () => { doAutosave(); };
  }, []); // eslint-disable-line

  useEffect(() => {
    const onBeforeUnload = (event) => {
      doAutosave();
      if (!shouldProtectExit) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [doAutosave, shouldProtectExit]);

  // ====== Responder ======
  const setAnswer = (qId, value) => {
    if (interactionBlocked) return;
    setRespuestas(prev => ({ ...prev, [qId]: value }));
  };

  // ====== UI ======
  if (loading) {
    return (
      <div className="test-wrap">
        <div className="skeleton title" />
        <div className="skeleton row" />
        <div className="skeleton card" />
        <style>{css}</style>
      </div>
    );
  }

  if (resultado) {
    return (
      <div className="test-container">
        <AssessmentResultCard
          title="Resultado del test"
          subtitle="Tu intento ha sido registrado correctamente."
          resultado={resultado}
          primaryActionText="Volver al curso"
          onPrimaryAction={() => navigate(`/curso/${codigoCurso}`)}
          showCoins
        />
        <style>{css}</style>
      </div>
    );
  }

  return (
    <div className="test-container">
      <header className="test-header">
        <div className="th-left">
          <h1>Test del Nivel</h1>
          <p className="muted">Responde y envía. No hay pistas ni retroalimentación.</p>
        </div>

        <div className="th-right">
          <div className="test-meta">
            {meta?.tiempoLimiteMin ? (
              <div className={`timer ${secondsLeft <= 30 ? 'warn' : ''}`}>
                <Clock size={18} /> <span>{formatTime(secondsLeft || 0)}</span>
              </div>
            ) : null}

            <div className="chips">
              <span className="chip">{answeredCount}/{total} respondidas</span>

              {meta?.porcentajeAprobacion ? (
                <span className="chip">Aprobación: {meta.porcentajeAprobacion}%</span>
              ) : null}
              {meta?.notaMaxima != null ? (
                <span className="chip">
                  Nota máx.: {meta.notaMaxima}
                </span>
              ) : null}

              {coinInfo.ready && (
                <span
                  className={`chip ${coinInfo.eligible ? 'chip-win' : 'chip-lock'}`}
                  title={
                    coinInfo.eligible
                      ? (coinInfo.premio > 0
                        ? `Si apruebas, ganas ${coinInfo.premio} UTMCoins`
                        : 'Si apruebas, ganas UTMCoins')
                      : 'Este intento no otorga UTMCoins'
                  }
                >
                  {coinInfo.eligible
                    ? (coinInfo.premio > 0 ? `Con UTMCoins (+${coinInfo.premio})` : 'Con UTMCoins')
                    : 'Sin UTMCoins'}
                </span>
              )}
            </div>
          </div>

          <div className="global-progress">
            <div className="gp-track">
              <div className="gp-fill" style={{ width: `${progressPct}%` }} />
            </div>
            <span className="gp-label">{progressPct}% completado</span>
          </div>
        </div>
      </header>

      {/* ===================== LAYOUT PRINCIPAL (PREGUNTA IZQ + SIDEBAR DER) ===================== */}
      <div className="nt-layout">
        {/* ===== Preguntas (IZQUIERDA) ===== */}
        <main className="nt-main">
          {current && (
            <div className="question-card">
              <div className="q-head">
                <span className="q-index">Pregunta {index + 1} de {total}</span>
                <span className="q-type">{labelTipo(current.tipo)}</span>
              </div>

              <PreguntaExamTestRenderer
                key={current.id}
                question={current}
                value={respuestas[current.id]}
                onChange={(val) => setAnswer(current.id, val)}
              />

              <div className="q-actions sticky">
                <button className="btn ghost" onClick={handlePrev} disabled={isFirst || interactionBlocked}>
                  <ChevronLeft size={18} /> <span className="btn-label">Anterior</span>
                </button>

                <div className="q-actions-center">
                  <span className="muted small">
                    {answeredCount}/{total} respondidas
                  </span>
                </div>

                <button
                  className={`btn primary ${isLast ? "send" : ""}`}
                  onClick={() => (isLast ? handleFinish(false) : handleNext())}
                  disabled={submitting || interactionBlocked}
                >
                  {isLast ? (
                    <>
                      <Send size={18} /> <span className="btn-label">Enviar Test</span>
                    </>
                  ) : (
                    <>
                      <span className="btn-label">Siguiente</span> <ChevronRight size={18} />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </main>

        {/* ===== Sidebar (DERECHA) ===== */}
        <aside className="nt-side">
          <div className="nt-side-sticky">

            {/* Navegación por preguntas */}
            <div className="nt-nav-card">
              <div className="nt-nav-title">Navegación</div>

              <div className="nav-questions">
                {preguntas.map((q, i) => {
                  const v = respuestas[q.id];
                  const done = Array.isArray(v) ? v.length > 0 : !!(v && String(v).trim());
                  return (
                    <button
                      key={q.id}
                      className={`nav-dot ${i === index ? 'active' : ''} ${done ? 'done' : ''}`}
                      onClick={() => goTo(i)}
                      title={`Ir a pregunta ${i + 1}`}
                    >
                      {i + 1}
                    </button>
                  );
                })}
              </div>

              <div className="nt-nav-footer">
                <span className="muted small">{progressPct}% completado</span>
              </div>
            </div>

            {/* Vigilancia / Proctor */}
            {REQUIRE_PROCTOR && !preflightOpen && (
              <>
                <NivelTestProctorPanel
                  proctor={proctor}
                  deviceOpts={proctorDevices}
                  videoRef={proctorVideoRef}
                  onRetry={() => retryProctor(selectedCameraId || "")}
                  onSelectCam={handleCameraChange}
                  selectedCamId={selectedCameraId}
                  switchingCamera={switchingCamera}
                />
                <div className="nt-side-hint">
                  Mantén la cámara activa durante todo el test.
                </div>
              </>
            )}
          </div>
        </aside>
      </div>

      {/* ===== Overlay preflight ===== */}
      {preflightOpen && (
        <div className="nt-secureWaitShell" role="dialog" aria-modal="true">
          <div className="nt-deviceModal">
            <div className="nt-modalTitle">
              {resumeContext.isResume ? "Continuar test de nivel" : "Preparar test de nivel"}
            </div>

            <div className="nt-modalBody">
              {REQUIRE_PROCTOR
                ? 'Revisa tu progreso, confirma la cámara seleccionada y valida el encuadre antes de ingresar.'
                : 'Revisa tu progreso e indicaciones de seguridad antes de ingresar al test.'}
            </div>

            <div className="nt-secure-steps" style={{ marginTop: 14, marginBottom: 8 }}>
              <div className="nt-secure-step">
                <strong>{resumeContext.isResume ? "1" : "•"}</strong>
                <span>
                  {resumeContext.isResume
                    ? `Reanudarás tu intento desde la pregunta ${Math.min((resumeContext.currentIndex || 0) + 1, Math.max(1, resumeContext.total || 1))} con ${resumeContext.answeredCount}/${resumeContext.total || 0} respuestas guardadas.`
                    : "Ingresarás a un nuevo intento del test de nivel con validación previa."}
                </span>
              </div>
              <div className="nt-secure-step">
                <strong>{resumeContext.isResume ? "2" : "•"}</strong>
                <span>
                  {REQUIRE_PROCTOR
                    ? effectiveCameraValidated
                      ? "La cámara actual ya está validada o lista para continuar."
                      : "Debes validar luz, encuadre y movimiento de la cámara antes de ingresar."
                    : "Este nivel no exige validación de cámara para continuar."}
                </span>
              </div>
            </div>

            {REQUIRE_PROCTOR ? (
              <>
                <div className="nt-hardwareRow" style={{ margin: "16px 0" }}>
                  <span style={{ fontSize: 13, color: "#475569", fontWeight: 800 }}>Cámara Web</span>
                  <select
                    className="ntp-select"
                    value={selectedCameraId}
                    onChange={(e) => handleCameraChange(e.target.value || "")}
                    disabled={switchingCamera}
                    style={{ flex: 1, maxWidth: 280 }}
                  >
                    <option value="">Automática (predeterminada)</option>
                    {(proctorDevices?.cams || []).map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label || "Cámara"}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="nt-facePreviewLayout">
                  <div
                    className={[
                      "nt-facePreviewRing",
                      getCameraValidationTone(cameraHealth),
                    ].filter(Boolean).join(" ")}
                  >
                    <video
                      ref={proctorVideoRef}
                      className="nt-devicePreviewVideo"
                      muted
                      playsInline
                      autoPlay
                    />

                    <div className="nt-faceGuide" />
                    <div className="nt-faceCenterDot" />

                    {cameraHealth?.validating ? (
                      <div className="nt-faceScanning">
                        <span />
                      </div>
                    ) : null}
                  </div>

                  <div className="nt-facePreviewInfo">
                    <div className={[
                      "nt-devicePreviewStatus",
                      getCameraValidationTone(cameraHealth),
                    ].filter(Boolean).join(" ")}>
                      <b>{getCameraValidationLabel(cameraHealth)}</b>

                      <span>
                        Coloca tu rostro dentro del círculo, mira de frente y muévete ligeramente.
                      </span>
                    </div>

                    <div className="nt-cameraChecks">
                      <div className={["nt-cameraCheck", cameraCheckOk(cameraHealth?.videoReady) ? "ok" : "", cameraCheckBad(cameraHealth?.videoReady) ? "bad" : ""].join(" ")}>
                        <span /> Imagen activa
                      </div>
                      <div className={["nt-cameraCheck", cameraHealth?.avgLuma != null && cameraHealth.avgLuma >= CAMERA_HEALTH_LIMITS.stableLumaMin && !cameraHealth.dark ? "ok" : "", cameraHealth?.dark ? "bad" : ""].join(" ")}>
                        <span /> Luz suficiente {cameraHealth?.avgLuma != null ? `(${cameraHealth.avgLuma}/255)` : ""}
                      </div>
                      <div className={["nt-cameraCheck", cameraHealth?.contrast != null && cameraHealth.contrast >= CAMERA_HEALTH_LIMITS.contrastMin && !cameraHealth.lowContrast ? "ok" : "", cameraHealth?.lowContrast ? "bad" : ""].join(" ")}>
                        <span /> Imagen clara {cameraHealth?.contrast != null ? `(${cameraHealth.contrast})` : ""}
                      </div>
                      <div className={["nt-cameraCheck", cameraHealth?.faceVisible === true ? "ok" : "", cameraHealth?.faceChecked && cameraHealth?.faceVisible === false ? "bad" : ""].join(" ")}>
                        <span /> {String(cameraHealth?.faceSource || "").includes("quality") ? "Encuadre visual aprobado" : "Rostro visible"}
                      </div>
                      <div className={["nt-cameraCheck", Number(cameraHealth?.motionScore || 0) >= CAMERA_HEALTH_LIMITS.motionDiffMin || Number(cameraHealth?.motionHits || 0) > 0 ? "ok" : ""].join(" ")}>
                        <span /> Movimiento leve {cameraHealth?.motionScore != null ? `(${cameraHealth.motionScore})` : ""}
                      </div>
                    </div>

                    {cameraHealth?.reason ? (
                      <div className={`nt-cameraReason ${cameraHealth?.ok ? "" : "bad"}`}>
                        {cameraHealth.reason}
                      </div>
                    ) : null}

                    {effectiveCameraValidated && cameraHealth?.validated !== true ? (
                      <div className="nt-cameraPreviewStatus ok" style={{ marginTop: 10 }}>
                        <b>Validación recuperada</b>
                        <span>Ya habías validado esta cámara en esta sesión. Puedes continuar o volver a validarla si deseas.</span>
                      </div>
                    ) : null}

                    <button
                      type="button"
                      className="nt-validateBtn"
                      onClick={validateCameraFromModal}
                      disabled={switchingCamera || cameraHealth?.validating || !cameraHealth?.videoReady}
                    >
                      {cameraHealth?.validating ? "Validando…" : cameraHealth?.validated ? "Validar otra vez" : "Validar cámara"}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="nt-devicePreviewStatus ok" style={{ marginTop: 16 }}>
                <b>Cámara no requerida</b>
                <span>Puedes ingresar al test sin validación de cámara para este nivel.</span>
              </div>
            )}

            <div className="nt-secure-steps" style={{ marginTop: 24 }}>
              <div className="nt-secure-step">
                <strong>•</strong>
                <span>Mantén el test en pantalla completa y no cambies de pestaña.</span>
              </div>
              <div className="nt-secure-step">
                <strong>•</strong>
                <span>Si sales del modo seguro, el test se bloqueará y puede enviarse automáticamente.</span>
              </div>
            </div>

            <label className="nt-secure-check" style={{ marginTop: 16 }}>
              <input
                type="checkbox"
                checked={preflightChecked}
                onChange={(e) => setPreflightChecked(e.target.checked)}
                disabled={REQUIRE_PROCTOR && !effectiveCameraValidated}
                style={{ width: 18, height: 18, accentColor: '#2563eb' }}
              />
              Confirmo que mantendré el test en modo seguro.
            </label>

            <div className="nt-deviceActions">
              <button
                className="btn ghost"
                type="button"
                onClick={() => navigate(`/curso/${codigoCurso}`)}
                disabled={preflightStarting}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="nt-secureEnterBtn"
                onClick={handleStartSecureMode}
                disabled={!preflightChecked || preflightStarting || (REQUIRE_PROCTOR && !effectiveCameraValidated)}
                title={REQUIRE_PROCTOR && !effectiveCameraValidated ? "Primero debes validar la cámara" : ""}
              >
                {preflightStarting ? "Preparando…" : resumeContext.isResume ? "CONTINUAR TEST" : "INGRESAR AL TEST"} <span>▷</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Overlay bloqueo ===== */}
      {REQUIRE_PROCTOR && secureModeReady && !preflightOpen && proctor?.blocked && (
        <div className="ntp-overlay">
          <div className="ntp-modal">
            <div className="ntp-title">🛑 Cámara requerida</div>
            <div className="ntp-text">
              Para continuar el Test de Nivel debes mantener la <b>cámara activa</b>.
            </div>

            {proctor?.reason ? (
              <div className="ntp-muted" style={{ marginTop: 6 }}>
                Motivo: {proctor.reason}
              </div>
            ) : null}

            <div style={{ marginTop: 12 }}>
              <label className="ntp-label">
                Cámara
                <select
                  className="ntp-select"
                  value={selectedCameraId}
                  onChange={(e) => handleCameraChange(e.target.value || "")}
                >
                  <option value="">Automática (predeterminada)</option>
                  {(proctorDevices?.cams || []).map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || "Cámara"}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="ntp-actions">
              <button className="ntp-btn" onClick={() => retryProctor?.()}>
                Reintentar cámara
              </button>
            </div>
          </div>
        </div>
      )}

      {enableKiosk && secureModeReady && !preflightOpen && kiosk?.blocked && (
        <div className="ntp-overlay">
          <div className="ntp-modal">
            <div className="ntp-title">⚠️ Vuelve al test</div>
            <div className="ntp-text">
              Debes mantener el test en foco y en pantalla completa.
            </div>

            <div className="ntp-muted" style={{ marginTop: 6 }}>
              {kiosk?.reason === "visibility_hidden" && "Cambiaste de pestaña o la ventana se ocultó."}
              {kiosk?.reason === "window_blur" && "Saliste de la ventana del test."}
              {kiosk?.reason === "fullscreen_exit" && "Saliste de pantalla completa."}
              {kiosk?.reason === "hotkey_navigation" && "Se detectó un atajo no permitido."}
              {!["visibility_hidden", "window_blur", "fullscreen_exit", "hotkey_navigation"].includes(kiosk?.reason) &&
                "Regresa al modo seguro para continuar."}
            </div>

            <div className="ntp-actions">
              <button
                className="ntp-btn"
                onClick={() => activateSecureMode({ fromRestore: true })}
                disabled={restoringSecureMode}
              >
                {restoringSecureMode ? "Restaurando..." : "Volver al modo seguro"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmActionModal
        open={finishConfirmOpen}
        title="Enviar respuestas"
        message="¿Deseas enviar tus respuestas? No podrás modificarlas después."
        confirmText="Sí, enviar"
        cancelText="Seguir respondiendo"
        tone="success"
        loading={submitting}
        onClose={() => setFinishConfirmOpen(false)}
        onConfirm={() => handleFinish(false, true)}
      />

      <style>{css}</style>
    </div>
  );
};

/* ----------------- Helpers ----------------- */
const labelTipo = (t) => {
  switch ((t || '').toLowerCase()) {
    case 'seleccion_multiple': return 'Selección múltiple';
    case 'verdadero_falso': return 'Verdadero / Falso';
    case 'respuesta_corta': return 'Respuesta corta';
    case 'completar': return 'Completar espacios';
    case 'ordenamiento': return 'Ordenamiento';
    case 'emparejamiento': return 'Emparejamiento';
    default: return t || 'Pregunta';
  }
};

const css = `
.test-container{
  max-width: 1180px;
  margin: 0 auto;
  padding: 18px;
}

/* Header */
.test-header{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:16px;
  margin-bottom: 12px;
  flex-wrap:wrap;
}
.th-left{
  min-width:0;
  flex:1 1 auto;
}
.th-right{
  min-width:0;
  flex:1 1 auto;
  display:flex;
  flex-direction:column;
  gap:10px;
}
.test-header h1{
  margin:0;
  font-size: clamp(1.1rem, 0.4vw + 1rem, 1.4rem);
  color:#0b3b7a;
  font-weight: 800;
}
.muted{
  color:#6b7280;
}
.muted.small{
  font-size:.85rem;
}

/* meta */
.test-meta{
  display:flex;
  flex-wrap:wrap;
  align-items:flex-start;
  gap:10px 12px;
}
.timer{
  display:flex;
  align-items:center;
  gap:6px;
  font-weight:800;
  border:1px solid #e5e7eb;
  border-radius:999px;
  padding:6px 10px;
  background:#fff;
  font-size:.9rem;
  line-height:1.2;
}
.timer.warn{
  color:#b91c1c;
  border-color:#fecaca;
  background:#fff1f2;
}
.chips{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
}
.chip{
  border:1px solid #e5e7eb;
  border-radius:999px;
  padding:6px 10px;
  font-weight:700;
  background:#fff;
  font-size:.8rem;
  line-height:1.2;
  white-space:nowrap;
}

/* progreso global */
.global-progress{
  margin-top:4px;
  display:flex;
  align-items:center;
  gap:10px;
  flex-wrap:wrap;
}
.gp-track{
  flex:1;
  background:#f2f4f7;
  border-radius:999px;
  height:10px;
  overflow:hidden;
  box-shadow: inset 0 1px 2px rgba(0,0,0,.04);
  min-width:120px;
}
.gp-fill{
  height:100%;
  width:0;
  background:#2563eb;
  transition: width .35s ease;
}
.gp-label{
  font-size:.85rem;
  color:#6b7280;
  min-width:max-content;
  text-align:right;
}
/* ================== LAYOUT NUEVO (IZQ pregunta / DER sidebar) ================== */
.nt-layout{
  display:grid;
  grid-template-columns: 1fr 340px;
  gap:14px;
  align-items:start;
  margin-top: 10px;
}

.nt-main{
  min-width:0;
}

.nt-side{
  min-width:0;
}

.nt-side-sticky{
  position: sticky;
  top: 14px;
  display:flex;
  flex-direction:column;
  gap:12px;
}

/* Card navegación */
.nt-nav-card{
  background:#fff;
  border:1px solid #e5e7eb;
  border-radius:18px;
  padding:12px;
  box-shadow:0 8px 22px rgba(0,0,0,.05);
}

.nt-nav-title{
  font-weight:950;
  color:#111827;
  margin-bottom:10px;
  font-size:14px;
}

.nt-nav-footer{
  margin-top:10px;
  display:flex;
  justify-content:space-between;
  align-items:center;
}

.nt-side-hint{
  font-size:12px;
  color:#6b7280;
  border:1px solid #e5e7eb;
  background:#fff;
  border-radius:14px;
  padding:10px 12px;
}
.nt-main{
  min-width:0;
}

/* nav preguntas */
.nav-questions{
  display:flex;
  flex-wrap:wrap;
  gap:6px;
  margin: 0 0 14px;
  max-width:100%;
}
.nav-dot{
  width:36px;
  height:36px;
  border-radius:10px;
  border:1px solid #e5e7eb;
  background:#fff;
  font-weight:800;
  transition: all .2s ease;
  font-size:.9rem;
  line-height:36px;
  text-align:center;
  flex:0 0 auto;
}
.nav-dot:hover{
  transform: translateY(-2px);
  box-shadow:0 6px 18px rgba(0,0,0,.06);
}
.nav-dot.active{
  border-color:#c7d2fe;
  background:#eef2ff;
  color:#3730a3;
}
.nav-dot.done{
  border-color:#bbf7d0;
  background:#ecfdf5;
  color:#065f46;
}

/* tarjeta pregunta */
.question-card{
  background:#fff;
  border:1px solid #e5e7eb;
  border-radius:18px;
  padding:18px;
  box-shadow:0 10px 30px rgba(0,0,0,.06);
}
.q-head{
  display:flex;
  align-items:center;
  justify-content:space-between;
  color:#6b7280;
  font-size:.9rem;
  gap:10px;
  flex-wrap:wrap;
}
.q-index{
  font-weight:800;
  color:#111827;
}
.q-type{
  border:1px dashed #e5e7eb;
  padding:4px 10px;
  border-radius:999px;
  background:#fff;
  font-weight:700;
  font-size:.8rem;
  line-height:1.2;
}

/* acciones pregunta */
.q-actions{
  display:flex;
  align-items:center;
  gap:10px;
  margin-top: 16px;
}
.q-actions.sticky{
  position: sticky;
  bottom: 12px;
  background:linear-gradient(180deg, rgba(255,255,255,.65), #fff);
  padding:10px;
  border-radius:14px;
  border:1px solid #e5e7eb;
  z-index:10;
}
.q-actions-center{
  flex:1;
  display:flex;
  justify-content:center;
}

/* botones */
.btn{
  display:inline-flex;
  align-items:center;
  gap:8px;
  padding:10px 14px;
  border-radius:12px;
  border:1px solid #e5e7eb;
  background:#fff;
  cursor:pointer;
  transition:.2s;
  font-size:.9rem;
  font-weight:700;
  line-height:1.2;
}
.btn-label{
  white-space:nowrap;
}
.btn:hover{
  transform: translateY(-1px);
  box-shadow:0 10px 24px rgba(0,0,0,.06);
}
.btn.ghost{
  background:#fff;
}
.btn.primary{
  background:#2563eb;
  color:#fff;
  border-color:#2563eb;
}
.btn.primary.send{
  background:#0f766e;
  border-color:#0f766e;
}
.btn:disabled{
  opacity:.6;
  cursor:not-allowed;
}

/* resultado */
.result-card{
  text-align:center;
  background:#fff;
  border:1px solid #e5e7eb;
  border-radius:16px;
  padding: 28px;
  max-width:560px;
  margin: 40px auto;
  box-shadow:0 10px 30px rgba(0,0,0,.08);
}
.result-card h2{
  margin: 8px 0 10px;
  color:#0b3b7a;
  font-size:1.2rem;
  font-weight:800;
}
.big-score{
  font-size:48px;
  font-weight:950;
  color:#111827;
  line-height:1;
  margin: 10px 0 6px;
}
.big-score span{
  display:inline-block;
  padding:6px 12px;
  border-radius:12px;
  background:#eef2ff;
  color:#3730a3;
  border:1px solid #c7d2fe;
}
.result-grid{
  display:grid;
  grid-template-columns: repeat(4, minmax(0,1fr));
  gap:10px;
  margin-top:12px;
}
.result-grid > div{
  background:#fff;
  border:1px solid #e5e7eb;
  border-radius:12px;
  padding:10px;
}
.result-grid b{
  display:block;
  font-size:1.1rem;
}
.result-grid span{
  color:#6b7280;
  font-size:.85rem;
}
.ok{ color:#065f46; }
.fail{ color:#b91c1c; }

/* chips UTMCoins */
.chip-win{
  border-color:#b7e4c7;
  background:#ecfdf5;
  color:#047857;
  font-weight:800;
}
.chip-lock{
  border-color:#fde68a;
  background:#fffbeb;
  color:#92400e;
  font-weight:800;
}

/* loader */
.skeleton{
  background: linear-gradient(90deg,#f2f3f5 25%, #f6f7f9 37%, #f2f3f5 63%);
  background-size: 400% 100%;
  animation: shimmer 1.4s infinite;
  border-radius: 14px;
}
.skeleton.title{ height: 28px; max-width:320px; margin-bottom:12px; }
.skeleton.row{ height: 18px; max-width:200px; margin-bottom:12px; }
.skeleton.card{ height: 200px; max-width:100%; }
@keyframes shimmer {
  0% { background-position: 200% 0 }
  100% { background-position: -200% 0 }
}

/* ===== Proctor NivelTest ===== */
.ntp-card{
  margin: 0;
  background:#fff;
  border:1px solid #e5e7eb;
  border-radius:18px;
  padding:12px;
  box-shadow:0 8px 22px rgba(0,0,0,.05);
}
.ntp-head{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  margin-bottom:10px;
}
.ntp-badge{
  border:1px solid #e5e7eb;
  border-radius:999px;
  padding:4px 10px;
  font-weight:900;
  font-size:12px;
  background:#fff;
}
.ntp-badge.ok{ background:#ecfdf5; border-color:#a7f3d0; color:#065f46; }
.ntp-badge.bad{ background:#fff1f2; border-color:#fecaca; color:#991b1b; }

.ntp-video{
  width:100%;
  aspect-ratio: 4/3;
  border-radius:14px;
  background:#000;
  border:1px solid #e5e7eb;
  object-fit: cover;
}

.ntp-warn{
  margin-top:10px;
  border:1px solid #fecaca;
  background:#fff1f2;
  color:#991b1b;
  border-radius:12px;
  padding:10px;
}
.ntp-muted{
  color:#6b7280;
  font-size:12px;
}
.ntp-label{
  display:block;
  font-size:13px;
  margin-top:10px;
  color:#111827;
  font-weight:700;
}
.ntp-select{
  width:100%;
  margin-top:6px;
  padding:10px 12px;
  border-radius:12px;
  border:1px solid #e5e7eb;
  background:#fff;
}
.ntp-btn{
  margin-top:10px;
  width:100%;
  padding:10px 12px;
  border-radius:12px;
  border:1px solid #e5e7eb;
  background:#2563eb;
  color:#fff;
  font-weight:900;
  cursor:pointer;
}

.ntp-overlay{
  position: fixed;
  inset:0;
  background: rgba(0,0,0,.45);
  display:flex;
  align-items:center;
  justify-content:center;
  z-index:9999;
  padding:12px;
}
.ntp-modal{
  width:min(520px, 95vw);
  background:#fff;
  border-radius:18px;
  border:1px solid #e5e7eb;
  box-shadow:0 18px 50px rgba(0,0,0,.35);
  padding:14px;
}
.ntp-title{
  font-weight:950;
  font-size:18px;
  margin-bottom:6px;
}
.ntp-text{
  color:#374151;
  font-size:14px;
  line-height:1.45;
}
.ntp-actions{
  margin-top:12px;
}

/* ================== MEDIA QUERIES ================== */

/* Tablets */
@media (max-width: 980px){
  .test-header{
    flex-direction:column;
    align-items:flex-start;
  }
  .th-right{
    width:100%;
  }
  .result-grid{
    grid-template-columns: repeat(2, minmax(0,1fr));
  }
  .nt-layout{
    grid-template-columns: 1fr;
  }
  .nt-side-sticky{
    position: relative;
    top: auto;
  }
}

/* Celulares */
@media (max-width: 600px){
  .test-container{
    padding:16px 12px 80px;
  }

  .nav-questions{
    flex-wrap:nowrap;
    overflow-x:auto;
    -webkit-overflow-scrolling:touch;
    scrollbar-width:thin;
    padding-bottom:4px;
  }
  .nav-dot{
    width:32px;
    height:32px;
    line-height:32px;
    font-size:.8rem;
    border-radius:8px;
    flex:0 0 auto;
  }

  .question-card{
    padding:14px 14px 90px;
    border-radius:16px;
  }

  .q-actions.sticky{
    position:fixed;
    left:0;
    right:0;
    bottom:0;
    max-width:1180px;
    margin:0 auto;
    border-radius:16px 16px 0 0;
    box-shadow:0 -8px 30px rgba(0,0,0,.12);
    border:1px solid #e5e7eb;
    background:linear-gradient(180deg, rgba(255,255,255,.9) 0%, #fff 40%);
    padding:12px 16px calc(12px + env(safe-area-inset-bottom));
    z-index:1000;
    gap:12px;
  }

  .btn{
    flex:1;
    justify-content:center;
    min-height:44px;
    font-size:.9rem;
  }
  .q-actions{
    flex-wrap:wrap;
  }
  .q-actions-center{
    order:3;
    flex-basis:100%;
    justify-content:center;
    font-size:.8rem;
  }

  .gp-label{
    flex-basis:100%;
    text-align:left;
  }
}

/* Celulares muy pequeños */
@media (max-width: 400px){
  .timer{
    font-size:.8rem;
    padding:4px 8px;
  }
  .chip{
    font-size:.7rem;
    padding:4px 8px;
  }
  .btn-label{
    font-size:.8rem;
  }
  .gp-label{
    display:none;
  }
}

.nt-secureWaitShell{
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(2, 6, 23, .58);
  display: grid;
  place-items: center;
  padding: 16px;
  overflow-y: auto;
}

.nt-secureWaitCard{
  width: min(880px, 100%);
  background: #fff;
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  box-shadow: 0 16px 34px rgba(15,23,42,.14);
  overflow: hidden;
}

.nt-secureWaitHead{
  display: flex;
  gap: 18px;
  align-items: center;
  padding: 32px 28px;
  border-bottom: 1px solid #e5e7eb;
}

.nt-secureIcon{
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #2563eb;
  font-size: 22px;
}

.nt-secureKicker{
  font-size: 10px;
  letter-spacing: .18em;
  font-weight: 900;
  color: #64748b;
  text-transform: uppercase;
}

.nt-secureWaitHead h1{
  margin: 6px 0 0;
  font-size: 22px;
  font-weight: 950;
  color: #1f2937;
}

.nt-secureWaitBody{
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px;
  padding: 28px;
}

@media (max-width: 768px) {
  .nt-secureWaitBody {
    grid-template-columns: 1fr;
  }
}

.nt-stepTitle{
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  font-weight: 950;
  color: #1f2937;
  margin-bottom: 18px;
}

.nt-stepTitle span{
  width: 22px;
  height: 22px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #2563eb;
  font-size: 12px;
}

.nt-hardwareBox{
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;
  display: grid;
  gap: 18px;
}

.nt-hardwareRow{
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-size: 14px;
  font-weight: 800;
  color: #334155;
}

.nt-hardwareRow b{
  font-size: 12px;
}
.nt-hardwareRow b.ok{ color: #059669; }
.nt-hardwareRow b.bad{ color: #dc2626; }

.nt-validateBtn{
  height: 40px;
  border: none;
  border-radius: 12px;
  background: #e0ecff;
  color: #2563eb;
  font-weight: 900;
  cursor: pointer;
  margin-top: 10px;
  transition: all 0.2s;
}

.nt-validateBtn:disabled{
  opacity: 0.6;
  cursor: not-allowed;
}

.nt-validateBtn:hover:not(:disabled){
  background: #bfdbfe;
}

.nt-secureFooter{
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 28px;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
  gap: 12px;
}

.nt-secureEnterBtn{
  min-width: 238px;
  height: 46px;
  border: none;
  border-radius: 14px;
  background: #1f2937;
  color: #fff;
  font-weight: 950;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
.q-actions{
    flex-wrap:wrap;
  }
  .q-actions-center{
    order:3;
    flex-basis:100%;
    justify-content:center;
    font-size:.8rem;
  }

  .gp-label{
    flex-basis:100%;
    text-align:left;
  }
}

/* Celulares muy pequeños */
@media (max-width: 400px){
  .timer{
    font-size:.8rem;
    padding:4px 8px;
  }
  .chip{
    font-size:.7rem;
    padding:4px 8px;
  }
  .btn-label{
    font-size:.8rem;
  }
  .gp-label{
    display:none;
  }
}

.nt-secureWaitShell{
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(2, 6, 23, .58);
  display: grid;
  place-items: center;
  padding: 16px;
  overflow-y: auto;
}

.nt-deviceModal {
  width: min(720px, 94vw);
  background: #fff;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 20px 40px rgba(15,23,42,.12);
  display: flex;
  flex-direction: column;
}

.nt-modalTitle {
  font-size: 20px;
  font-weight: 900;
  color: #0f172a;
  margin-bottom: 6px;
}

.nt-modalBody {
  font-size: 14px;
  color: #475569;
  line-height: 1.5;
}

.nt-facePreviewLayout {
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 20px;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
}

.nt-facePreviewRing {
  width: 240px;
  height: 240px;
  justify-self: center;
  position: relative;
  border-radius: 999px;
  overflow: hidden;
  background: #020617;
  border: 5px solid #cbd5e1;
  box-shadow: 0 20px 45px rgba(15,23,42,.24), inset 0 0 0 1px rgba(255,255,255,.08);
}

.nt-facePreviewRing.ok {
  border-color: #10b981;
  box-shadow: 0 18px 42px rgba(16,185,129,.22), 0 0 0 7px rgba(16,185,129,.10);
}

.nt-facePreviewRing.bad {
  border-color: #ef4444;
  box-shadow: 0 18px 42px rgba(239,68,68,.20), 0 0 0 7px rgba(239,68,68,.09);
}

.nt-facePreviewRing.checking {
  border-color: #3b82f6;
  box-shadow: 0 18px 42px rgba(59,130,246,.22), 0 0 0 7px rgba(59,130,246,.10);
}

.nt-devicePreviewVideo {
  width: 100%;
  height: 100%;
  display: block;
  border: none;
  border-radius: 999px;
  background: #020617;
  object-fit: cover;
  transform: scaleX(-1);
}

.nt-faceGuide {
  position: absolute;
  inset: 18px;
  border-radius: 999px;
  border: 1px dashed rgba(255,255,255,.72);
  pointer-events: none;
}

.nt-faceCenterDot {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 6px;
  height: 6px;
  transform: translate(-50%, -50%);
  border-radius: 999px;
  background: rgba(255,255,255,.65);
  box-shadow: 0 0 0 5px rgba(255,255,255,.12);
  pointer-events: none;
}

@keyframes ntScanFace {
  0% { transform: translateY(-100%); }
  100% { transform: translateY(100%); }
}

.nt-faceScanning {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(180deg, transparent 0%, rgba(59,130,246,.18) 48%, transparent 100%);
  animation: ntScanFace 1.4s ease-in-out infinite;
}

.nt-facePreviewInfo {
  min-width: 0;
  display: grid;
  gap: 12px;
}

.nt-devicePreviewStatus {
  display: grid;
  gap: 5px;
  font-size: 12px;
  color: #64748b;
  font-weight: 800;
  line-height: 1.35;
}

.nt-devicePreviewStatus b {
  font-size: 14px;
  color: #334155;
}

.nt-devicePreviewStatus.ok b { color: #047857; }
.nt-devicePreviewStatus.bad b { color: #b91c1c; }
.nt-devicePreviewStatus.checking b { color: #1d4ed8; }

.nt-deviceActions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.nt-cameraChecks {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
}

.nt-cameraCheck {
  display: flex;
  align-items: center;
  gap: 7px;
  min-height: 34px;
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #64748b;
  font-size: 12px;
  font-weight: 850;
}

.nt-cameraCheck span {
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: #cbd5e1;
  flex: 0 0 auto;
}

.nt-cameraCheck.ok {
  border-color: #bbf7d0;
  background: #f0fdf4;
  color: #047857;
}

.nt-cameraCheck.ok span {
  background: #10b981;
}

.nt-cameraCheck.bad {
  border-color: #fecaca;
  background: #fef2f2;
  color: #b91c1c;
}

.nt-cameraCheck.bad span {
  background: #ef4444;
}

@media (max-width: 720px) {
  .nt-facePreviewLayout {
    grid-template-columns: 1fr;
  }
  .nt-facePreviewRing {
    width: 200px;
    height: 200px;
  }
  .nt-cameraChecks {
    grid-template-columns: 1fr;
  }
  .nt-deviceActions {
    flex-direction: column;
  }
  .nt-deviceActions button {
    width: 100%;
  }
}

.nt-cameraReason{
  margin-top: 10px;
  padding: 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.nt-cameraPreviewStatus{
  display:grid;
  gap:6px;
  padding:12px 14px;
  border-radius:12px;
  border:1px solid #dbe3ef;
  background:#f8fafc;
  color:#475569;
  font-size:13px;
}

.nt-cameraPreviewStatus b{
  font-size:14px;
  color:#1f2937;
}

.nt-cameraPreviewStatus.ok{
  border-color:#bbf7d0;
  background:#f0fdf4;
  color:#166534;
}

.nt-cameraPreviewStatus.ok b{
  color:#166534;
}

.nt-cameraPreviewStatus.bad{
  border-color:#fecaca;
  background:#fef2f2;
  color:#991b1b;
}

.nt-cameraPreviewStatus.bad b{
  color:#991b1b;
}

.nt-cameraPreviewStatus.checking{
  border-color:#bfdbfe;
  background:#eff6ff;
  color:#1d4ed8;
}

.nt-cameraPreviewStatus.checking b{
  color:#1d4ed8;
}

.nt-secure-steps{
  display:grid;
  gap:10px;
  margin-top:16px;
  margin-bottom:16px;
}

.nt-secure-step{
  display:flex;
  gap:10px;
  align-items:flex-start;
  border:1px solid #e5e7eb;
  background:#f8fafc;
  border-radius:14px;
  padding:12px;
}

.nt-secure-step strong{
  color:#1d4ed8;
}

.nt-secure-check{
  display:flex;
  align-items:flex-start;
  gap:10px;
  margin-top:14px;
  color:#334155;
  font-weight:600;
}

.nt-secure-proctor--compact .ntp-card{
  padding: 10px;
  border-radius: 16px;
  box-shadow: none;
}

.nt-secure-proctor--compact .ntp-head{
  margin-bottom: 8px;
}

.nt-secure-proctor--compact .ntp-video{
  width: 100%;
  max-width: 320px;
  aspect-ratio: 16 / 9;
  margin: 0 auto;
  display: block;
  border-radius: 12px;
}

.nt-secure-proctor--compact .ntp-label{
  margin-top: 8px;
  font-size: 12px;
}

.nt-secure-proctor--compact .ntp-select{
  padding: 8px 10px;
}

.nt-secure-proctor--compact .ntp-btn{
  margin-top: 8px;
  padding: 9px 10px;
}

.nt-secure-card{
  width: min(700px, 100%);
  padding: 18px;
}

.nt-secure-steps{
  gap: 8px;
  margin-top: 14px;
  margin-bottom: 14px;
}

.nt-secure-step{
  padding: 10px 12px;
}

.ntp-controls{
  margin-top: 10px;
  display: grid;
  gap: 10px;
}

.ntp-card--compact .ntp-video{
  max-width: 320px;
  aspect-ratio: 16 / 9;
  margin: 0 auto;
  display: block;
}
`;

export default NivelTest;
